import { Button } from './Button';

function FilterFunnelIcon({ className = 'h-[14px] w-[14px]' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 3H2l8 9.5V19l4 2v-8.5L22 3z" />
    </svg>
  );
}

export interface FilterToggleButtonProps {
  /** Fully resolved label, e.g. "Filters" or "Filters (2)" */
  label: string;
  expanded: boolean;
  onClick: () => void;
}

/**
 * FilterToggleButton — the desktop list-toolbar "Filters" button: funnel icon + label, toggling a
 * collapsible filter panel below it. Established by the patient directory; reuse this rather than
 * hand-rolling the icon + ghost button per feature (see .ai/ui-conventions.md §13).
 */
export function FilterToggleButton({ label, expanded, onClick }: FilterToggleButtonProps) {
  return (
    <Button type="button" variant="ghost" aria-expanded={expanded} onClick={onClick}>
      <FilterFunnelIcon />
      <span className="ml-1.5">{label}</span>
    </Button>
  );
}

export interface MobileFilterButtonProps {
  /** Accessible label — should include the active count when > 0, e.g. "Filters (2)" */
  ariaLabel: string;
  activeCount?: number;
  expanded: boolean;
  onClick: () => void;
}

/**
 * MobileFilterButton — the mobile list-toolbar filter entry point: a square icon-only button with a
 * badge showing the active-filter count, opening a BottomSheet. Established by the patient directory.
 */
export function MobileFilterButton({ ariaLabel, activeCount = 0, expanded, onClick }: MobileFilterButtonProps) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={ariaLabel}
      onClick={onClick}
      className="relative flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)]"
    >
      <FilterFunnelIcon className="h-[15px] w-[15px]" />
      {activeCount > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--primary)] px-1 text-[10px] font-bold text-[var(--primary-fg)]">
          {activeCount}
        </span>
      )}
    </button>
  );
}
